import { NON_EXISTING_USER_NAME, UNTITLED_ISSUE_TITLE, INDEX_FALLBACK } from "@overmap-ai/common"
import type { IssueTypeIdentifier, IssueTypePriority, IssueTypeStatus, Stored, User } from "@overmap-ai/common"
import { FieldSchema } from "@overmap-ai/forms"

import type { IssueData } from "../../typings"
import { getUserDisplayLabel } from "../../utils/users"
import { combineColumnDefs, CSVExporter } from "./CSVExporter"
import type { CSVColumnDef } from "./typings"
import { getCSVColumnDefsOfFieldSchema } from "./utils/fields"
import { getCSVColumnDefsOfIdentifiers } from "./utils/identifiers"

interface IssueDataCsvExporterConfig {
	issueTypeStatuses: Stored<IssueTypeStatus>[]
	issueTypePriorities: Stored<IssueTypePriority>[]
	issueTypeIdentifiers: Stored<IssueTypeIdentifier>[]
	issueTypeFieldSchema: FieldSchema
	users: User[]
}

export const getIssueDataCsvExporter = (data: IssueData[], config: IssueDataCsvExporterConfig) => {
	const { issueTypeStatuses, issueTypePriorities, issueTypeIdentifiers, issueTypeFieldSchema, users } = config

	const issueTypeStatusesById = Object.fromEntries(
		issueTypeStatuses.map((issueTypeStatus) => [issueTypeStatus.uuid, issueTypeStatus]),
	)
	const issueTypePrioritiesById = Object.fromEntries(
		issueTypePriorities.map((issueTypePriority) => [issueTypePriority.uuid, issueTypePriority]),
	)

	const usersById = Object.fromEntries(users.map((user) => [user.id, user]))

	const baseColumns: CSVColumnDef<IssueData>[] = [
		{ id: "index", header: "Index", accessorFn: (row) => row.issue.index ?? INDEX_FALLBACK },
		{ id: "title", header: "Title", accessorFn: (row) => row.issue.title || UNTITLED_ISSUE_TITLE },
		{
			id: "created_by",
			header: "Creator",
			accessorFn: (row) => {
				if (!row.issue.created_by) return NON_EXISTING_USER_NAME
				const user = usersById[row.issue.created_by]
				return getUserDisplayLabel(user)
			},
		},
		{ id: "submitted_at", header: "Submitted at", accessorFn: (row) => row.issue.submitted_at },
		{
			id: "status",
			header: "Status",
			accessorFn: (row) => {
				const issueTypeStatus = issueTypeStatusesById[row.issue.status]
				return issueTypeStatus.name
			},
		},
		{
			id: "priority",
			header: "Priority",
			accessorFn: (row) => {
				const issueTypePriority = issueTypePrioritiesById[row.issue.priority]
				return issueTypePriority?.name ?? null
			},
		},
		{
			id: "assigned_to",
			header: "Assigned to",
			accessorFn: (row) => {
				if (!row.issue.assigned_to) return ""
				const user = usersById[row.issue.assigned_to]
				return getUserDisplayLabel(user)
			},
		},
		{ id: "due_date", header: "Due date", accessorFn: (row) => row.issue.due_date ?? "" },
	]

	const issueTypeIdentifierColumns = getCSVColumnDefsOfIdentifiers<Stored<IssueTypeIdentifier>, IssueData>(
		issueTypeIdentifiers,
		(identifier) => (row) => row.issueTypeIdentifierValues[identifier.uuid]?.value,
	)

	const issueTypeFieldSchemaColumns = getCSVColumnDefsOfFieldSchema<IssueData>(
		issueTypeFieldSchema,
		(row) => row.issueTypeFieldValues?.values,
	)

	return new CSVExporter({
		columns: combineColumnDefs(baseColumns, issueTypeIdentifierColumns, issueTypeFieldSchemaColumns),
		data: data,
	})
}
